/**
 * Which component owns Apple Pay / Google Pay for a given payment provider.
 *
 *   trx → 'trx-library'  TRX's own buttons, mounted inside its JS form. TRX
 *                        supplies the gateway config itself, so checkout needs
 *                        no VITE_GOOGLE_PAY_* / VITE_APPLE_PAY_MERCHANT_ID.
 *
 * This is a PER-PROVIDER seam: a second processor adds its owner here (its own
 * library's buttons, or a sheet we drive). It cost every TRX restaurant its
 * wallet buttons in production once, when `enableWallets` was removed from the
 * TRX input on the assumption that another component would cover it — which is
 * why the rule lives in one testable function rather than in JSX conditions.
 * `walletOwner.test.ts` asserts the provider set stays covered, so a provider
 * cannot silently fall through.
 */

/** Wallet-capable payment providers. Extend as providers are onboarded. */
export const CONFIGURED_PAYMENT_PROVIDERS = ['trx'] as const;

export type ConfiguredPaymentProvider = (typeof CONFIGURED_PAYMENT_PROVIDERS)[number];

export type WalletOwner = 'trx-library' | 'none';

/**
 * Resolve the wallet owner. `'none'` ONLY for an unconfigured or unknown
 * provider — a configured one returning `'none'` means it renders no wallet
 * buttons at all, which is the production outage this function exists to
 * prevent.
 */
export function resolveWalletOwner(paymentProvider: string | null | undefined): WalletOwner {
    switch (paymentProvider) {
        case 'trx':
            return 'trx-library';
        default:
            return 'none';
    }
}
